<template>
  <main>
    <b-card no-body>
      <section name="template-name-and-detail" class="card-padding">
        <span v-if="!configTemplateId" class="card__header--topic"
          >Create New Template</span
        >
        <span v-else class="card__header--topic">Template Detail</span>
        <div class="custom-max-width mt-2">
          <atom-input
            v-model="targetTemplate.name"
            title="Template Name"
            placeholder="Template Name"
            :isAsterisk="true"
            :isError="isErrorInput.name"
            errorMessage="Please enter name"
            :isDisable="!hasUpdatePermission"
          ></atom-input>
          <atom-multi-select
            v-model="targetTemplate.tags"
            title="Tag"
            placeholder="Tag"
            label="tagName"
            class="mt-1"
            :isClearable="false"
            :isAppendToBody="false"
            :isEnableProper="false"
            :isPushTaggable="true"
            :option="tagOptions"
            :isDisable="!hasUpdatePermission"
          ></atom-multi-select>
          <atom-text-area
            v-model="targetTemplate.description"
            :isAsterisk="true"
            title="Description"
            placeholder="Description"
            :isError="isErrorInput.description"
            errorMessage="Please enter description"
            :isDisable="!hasUpdatePermission"
            class="mt-1"
          ></atom-text-area>
        </div>
      </section>
      <hr />
      <section name="config-template" class="card-padding">
        <div
          name="header-and-tooltip__wrapper"
          class="d-flex align-items-center mb-2"
        >
          <span class="custom-text--topic">Configuration Template</span>
          <div id="info-tooltip" class="info-icon_container ml-1">
            <feather-icon icon="InfoIcon" stroke="#6E6B7B" size="12" />
          </div>
          <b-tooltip
            target="info-tooltip"
            placement="right"
            v-ripple.400="'rgba(113, 102, 240, 0.15)'"
            custom-class="config-template--custom-tooltip-width"
          >
            <span
              v-html="
                'Variable format: ${{ VARIABLE_NAME }} example: ${{ IP_ADDRESS }}'
              "
            >
            </span>
          </b-tooltip>
        </div>
        <atom-text-area
          v-model="targetTemplate.config"
          :isAsterisk="true"
          :placeholder="`config t
interface \${{ INTERFACE_NAME }}
description \${{ DESCRIPTION }}
end`"
          :isDisable="!hasUpdatePermission"
          class="mt-1"
        ></atom-text-area>
      </section>
      <section
        name="button-section"
        class="card-padding mt-2 d-flex d-flex justify-content-between"
      >
        <div class="delete--button">
          <atom-button
            v-if="isShowDeleteButton && hasUpdatePermission"
            buttonId="delete-auto-config-button"
            :styleInButton="deleteButtonStyle"
            @click="onClickDeleteButton"
            ><feather-icon
              icon="Trash2Icon"
              class="delete__icon--style"
            />Delete</atom-button
          >
        </div>
        <div class="d-flex">
          <atom-button
            v-if="isShowUseTemplateButton && hasUpdatePermission"
            buttonId="use-template-button"
            @click="onClickUseConfigTemplateButton"
          >
            Use Template
          </atom-button>

          <atom-button
            v-if="isShowSaveChangesButton && hasUpdatePermission"
            buttonId="save-changes-config-template-button"
            :isDisable="isError"
            @click="onClickSaveChangesConfigTemplateButton"
          >
            Save Changes
          </atom-button>

          <atom-button
            v-if="isShowCreateNewConfigTemplateButton && hasUpdatePermission"
            class="custom-primary-button-width"
            buttonId="create-config-template-button"
            :isDisable="isError"
            @click="onClickCreateNewConfigTemplateButton"
          >
            Create
          </atom-button>

          <atom-button
            v-if="hasUpdatePermission"
            class="ml-1"
            buttonId="cancel-create-edit-command-button"
            buttonStyle="border"
            buttonColor="secondary"
            @click="onClickCancelCreateEditConfigTemplate && hasUpdatePermission"
          >
            Cancel
          </atom-button>
        </div>
      </section>
    </b-card>

    <!-- Modal Start-->
    <section name="modal">
      <molecule-modal-dialog
        modalId="create-config-template__confirm-create"
        :isTextCenter="true"
        :textTitle="`Do you want to create\n“${targetTemplate.name}” ?`"
        buttonTextLeft="Confirm"
        @clickButtonLeft="clickConfirmCreate"
      ></molecule-modal-dialog>

      <molecule-modal-dialog
        modalId="create-config-template__confirm-cancel"
        :isTextCenter="true"
        :textTitle="`Do you want to leave\nwithout finishing?`"
        buttonTextLeft="Confirm"
        @clickButtonLeft="clickConfirmCancel"
      ></molecule-modal-dialog>

      <molecule-modal-dialog
        modalId="edit-config-template__confirm-save"
        buttonTextLeft="Save Changes"
        :isTextCenter="true"
        :textTitle="`Do you want to save changes\n“${targetTemplate.name}” ?`"
        @clickButtonLeft="onClickConfirmSave"
      ></molecule-modal-dialog>

      <molecule-modal-dialog
        modalId="edit-config-template__confirm-delete"
        icon="AlertCircleIcon"
        :isTextCenter="true"
        :textTitle="`Do you want to delete\n${targetTemplate.name}?`"
        textSubtitle="You won’t be able to revert this!"
        buttonTextLeft="Delete"
        buttonTextRight="Cancel"
        buttonColorLeft="danger"
        @clickButtonLeft="onClickConfirmDelete"
      ></molecule-modal-dialog>
    </section>
    <!-- Modal End-->
  </main>
</template>

<script>
const replacer = (key, value) =>
  value instanceof Object && !(value instanceof Array)
    ? Object.keys(value)
        .sort()
        .reduce((sorted, key) => {
          sorted[key] = value[key]
          return sorted
        }, {})
    : value

import { BCard, VBTooltip, BTooltip } from "bootstrap-vue"
import AtomButton from "@/components/atoms/AtomButton.vue"
import AtomInput from "@/components/atoms/AtomInput.vue"
import AtomMultiSelect from "@/components/atoms/AtomMultiSelect.vue"
import AtomTextArea from "@/components/atoms/AtomTextArea.vue"
import Ripple from "vue-ripple-directive"
import mixinsValidator from "@/mixins/mixinsValidator"
import MoleculeModalDialog from "@/components/molecules/MoleculeModalDialog.vue"
import { PermissionUser } from "@/constants/permission-user"
import mixinsUser from "@/mixins/mixinsUser"


export default {
  name: "OrganismCreateEditConfigTemplate",
  components: {
    AtomButton,
    AtomInput,
    AtomMultiSelect,
    AtomTextArea,
    BCard,
    BTooltip,
    MoleculeModalDialog,
  },
  props: {
    tagOptions: {
      type: Array,
      required: false,
      default: [],
    },
    configTemplateDetail: {
      type: Object,
      required: true,
    },
  },
  directives: {
    "b-tooltip": VBTooltip,
    Ripple,
  },
  mixins: [mixinsValidator, mixinsUser],
  emits: ["createTemplate", "updateTemplate", "deleteTemplate"],
  data() {
    return {
      targetTemplate: {
        name: "",
        tags: [],
        description: "",
        config: "",
      },

      configTemplateScript: "config t\r\ninterface ${{INTERFACE_NAME}}\r\ndescription ${{DESCRIPTION}}\r\nend",

      isValidate: false,
      isErrorInput: {
        name: false,
        description: false,
      },

      deleteButtonStyle:
        "background-color: #F6E4E4 !important;color: #EA5455 !important; border-color: #F6E4E4 !important",
    }
  },
  computed: {
    hasUpdatePermission() {
      return this.hasPermission(PermissionUser.NCM.CONFIG_TEMPLATE_WRITE)
    },

    configTemplateId() {
      return this.$route?.params?.id
    },
    isShowSaveChangesButton() {
      return (
        this.hasString(this.configTemplateId) &&
        this.isConfigTemplateDetailChange
      )
    },
    isShowDeleteButton() {
      return this.hasString(this.configTemplateId)
    },
    isShowUseTemplateButton() {
      return (
        this.hasString(this.configTemplateId) && !this.isShowSaveChangesButton
      )
    },
    isShowCreateNewConfigTemplateButton() {
      return this.isEmptyString(this.configTemplateId)
    },
    isConfigTemplateDetailChange() {
      let originData = JSON.stringify(this.configTemplateDetail, replacer)
      let currentData = JSON.stringify(this.targetTemplate, replacer)
      return originData !== currentData
    },

    isError() {
      return this.isErrorInput.name || this.isErrorInput.description
    },
  },
  watch: {
    targetTemplate: {
      deep: true,
      handler() {
        this.validateRequireInput()
      },
    },
  },
  mounted() {
    if (this.hasString(this.configTemplateId)) {
      this.targetTemplate = JSON.parse(
        JSON.stringify(this.configTemplateDetail)
      )
    }
  },
  methods: {
    navigateTo(path) {
      this.$router.push(path)
    },

    // create
    onClickCreateNewConfigTemplateButton() {
      this.isValidate = true
      this.validateRequireInput()

      if (!this.isError)
        this.openModal("create-config-template__confirm-create")
    },
    clickConfirmCreate() {
      const createTemplate = {
        configTemplateName: this.targetTemplate.name,
        configTemplateDescription: this.targetTemplate.description,
        configTemplateScript: this.targetTemplate.config || this.configTemplateScript,
        tags: this.targetTemplate.tags,
      }

      this.$emit("createTemplate", createTemplate)

      this.closeModal()
    },

    // use template
    onClickUseConfigTemplateButton() {
      this.navigateTo(
        `/network-configuration/configuration-template/use-template/${this.configTemplateId}`
      )
    },

    // save
    onClickSaveChangesConfigTemplateButton() {
      this.isValidate = true
      this.validateRequireInput()

      if (!this.isError) this.openModal("edit-config-template__confirm-save")
    },
    onClickConfirmSave() {
      const updateTemplate = {
        configTemplateName: this.targetTemplate.name,
        configTemplateDescription: this.targetTemplate.description,
        configTemplateScript: this.targetTemplate.config || this.configTemplateScript,
        tags: this.targetTemplate.tags,
      }

      this.$emit("updateTemplate", updateTemplate)

      this.closeModal()
    },

    // cancel
    onClickCancelCreateEditConfigTemplate() {
      if (this.isShowSaveChangesButton && this.isConfigTemplateDetailChange)
        this.openModal("create-config-template__confirm-cancel")
      else this.clickConfirmCancel()
    },
    clickConfirmCancel() {
      this.closeModal()

      this.navigateTo("/network-configuration/configuration-template")
    },

    // delete
    onClickDeleteButton() {
      this.openModal("edit-config-template__confirm-delete")
    },
    onClickConfirmDelete() {
      this.$emit("deleteTemplate", this.configTemplateId)

      this.closeModal()
    },

    // validate
    validateRequireInput() {
      this.isErrorInput = {
        name: this.isValidate
          ? !this.hasString(this.targetTemplate.name)
          : false,
        description: this.isValidate
          ? !this.hasString(this.targetTemplate.description)
          : false,
      }
    },

    // modal
    openModal(id) {
      this.modalId = id
      this.$bvModal.show(this.modalId)
    },
    closeModal() {
      this.$bvModal.hide(this.modalId)
      this.modalId = null
    },
  },
}
</script>

<style lang="scss" scoped>
hr {
  margin-top: 1.5rem;
  margin-bottom: 0;
  border: 0;
  border-top: 1px solid #ebe9f1;
}

.card-padding {
  padding: 21px;
}

.card-padding-x {
  padding-left: 21px;
  padding-right: 21px;
}

.card-padding-y {
  padding-top: 21px;
  padding-bottom: 21px;
}

.card-padding-top {
  padding-top: 21px;
}

.card-padding-bottom {
  padding-bottom: 21px;
}

.card__header--topic {
  font-weight: 500;
  font-size: 18px;
}

.custom-text--topic {
  font-weight: 600;
  font-size: 14px;
}

.custom-tooltip-width {
  width: 500px;
}

.custom-primary-button-width {
  width: 125px;
}

.delete__icon--style {
  margin-right: 5px;
}

.delete--button ::v-deep {
  .btn-primary:hover:not(.disabled):not(:disabled) {
    box-shadow: none;
  }
}

// Small devices (landscape phones, 576px and up)
@media screen and (min-width: 576px) {
}

// Medium devices (tablets, 768px and up)
@media screen and (min-width: 768px) {
  .custom-max-width {
    max-width: 75%;
  }
}

// Large devices (desktops, 992px and up)
@media screen and (min-width: 992px) {
}
</style>
